/*
 * Figure Molecule Component
 *
 */

import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { useClickAway } from "../../../hooks/useClickAway"
import Button from "../../atoms/button/button"
import Icon from "../../atoms/icon/icon"

const Figure = props => {
  const [creditsOpen, setCreditsOpen] = useState(false)
  const [imageExpanded, setImageExpanded] = useState(false)

  const ref = useClickAway(() => {
    setImageExpanded(false)
  })

  const openCredits = () => {
    setCreditsOpen(!creditsOpen)
  }

  const expandImage = () => {
    setImageExpanded(!imageExpanded)
  }

  // Add no-scroll class to body tag if figure is expanded to prevent scroll behavior
  useEffect(() => {
    if (imageExpanded) {
      document.body.classList.add("no-scroll")
    } else {
      document.body.classList.remove("no-scroll")
    }
  }, [imageExpanded])

  return (
    <figure className={`figure ${imageExpanded ? "figure--expanded" : ""}`}>
      <div className="figure__placeholder" ref={ref}>
        <>
          {props.isVideo ? (
            <>
              <iframe
                className="figure__video"
                width="1600"
                height="900"
                src={props.filename}
                title="Video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </>
          ) : (
            <>
              <img
                className="figure__image"
                width="1600"
                height="900"
                src={props.filename}
                alt={props.imageALT}
              />
              <button
                className="figure__expand-button"
                aria-expanded={imageExpanded}
                aria-label={
                  imageExpanded
                    ? "close expanded figure"
                    : "expand figure to enlarge image"
                }
                onClick={expandImage}
              >
                <span>
                  <Icon
                    name={imageExpanded ? "close" : "expand"}
                    title={
                      imageExpanded
                        ? "close expanded figure"
                        : "expand figure to enlarge image"
                    }
                  />
                  <span className="sr-only">
                    {imageExpanded
                      ? "close expanded figure"
                      : "expand figure to enlarge image"}
                  </span>
                </span>
              </button>
            </>
          )}
          {props.imageCopyright ? (
            <div className="figure__footer">
              <div
                className={`figure__imageCredits ${
                  creditsOpen ? "figure__imageCredits--open" : ""
                }`}
              >
                <p>
                  <small>© copyright: {props.imageCopyright}.</small>
                  {props.imageSource ? (
                    <small>source: {props.imageSource}.</small>
                  ) : (
                    <></>
                  )}
                </p>
              </div>

              <Button
                className={`figure__credits-button ${
                  creditsOpen ? "figure__credits-button--active" : ""
                }`}
                icon="copyright"
                iconTitle="view image rights"
                srOnly="view image rights"
                onClick={openCredits}
                modifier="--credits-button"
                text=""
              />
            </div>
          ) : (
            <></>
          )}
        </>
      </div>
      {props.caption ? (
        <figcaption className="figure__caption">{props.caption}</figcaption>
      ) : (
        <></>
      )}
    </figure>
  )
}

Figure.defaultProps = {
  isVideo: false,
  filename: "",
  imageCopyright: "",
  imageSource: "",
  imageALT: "",
  caption: "",
}

Figure.propTypes = {
  isVideo: PropTypes.bool,
  filename: PropTypes.string.isRequired,
  imageCopyright: PropTypes.string,
  imageSource: PropTypes.string,
  imageALT: PropTypes.string.isRequired,
  caption: PropTypes.string,
}

export default Figure
